/* eslint-disable no-restricted-globals */
import {
  createRootRouteWithContext,
  Outlet,
  useNavigate,
  useLocation,
  useRouterState,
} from "@tanstack/react-router";
import React, { useEffect } from "react";
import * as Sentry from "@sentry/react";
import Nav from "@/components/common/Nav";
import { ImpersonationBanner } from "@/components/common/ImpersonationMenu";
import { useMeQuery } from "@/queries/queries";
import { Toaster } from "@/queries/toaster";
import { cn } from "@/lib/utils";
import { ChatSidebarProvider } from "@/components/chat/useChatSidebar";
import PortalReturnBar, {
  usePortalReturnMode,
} from "@/components/portal/PortalReturnBar";
import RootErrorFallback from "@/components/RootErrorFallback";
import { rootRedirect } from "@/lib/postLoginHome";

const RootComponent = () => {
  const { data: user, isSuccess, isPending } = useMeQuery();
  const location = useLocation();
  // The page the Outlet still shows: during a navigation it lags `location` until the target loads.
  const shown = useRouterState({ select: (s) => s.resolvedLocation ?? s.location });
  const navigate = useNavigate();
  // Portal-return mode: a Portal user dipped into Classic for a specific flow.
  // Activated by ?fromPortal=1 on the URL, persisted via sessionStorage.
  // Hook MUST stay above the `if (isPending) return null` early-return so the
  // hook count is stable across loading/loaded renders.
  const portalReturnMode = usePortalReturnMode();

  const unauthenticatedRoutes = [
    "/login",
    "/policies/data_usage",
    "/policies/tos",
    "/policies/privacy",
    "/external/auth_success",
    "/external/utility_scope_selection",
    "/external/initiate_utility_auth",
    "/external/new_utility_connection",
    "/external/password_reset_request",
    "/external/password_reset_confirm",
  ];

  const isUnauthPath = (pathname: string) =>
    unauthenticatedRoutes.includes(pathname) ||
    pathname.includes("account_activation") ||
    pathname.startsWith("/owner");
  const isUnauthRoute = isUnauthPath(location.pathname);

  // /portal/* is authed (PortalLayout owns its own chrome) — keep prod's
  // SidebarNav OUT so the two visual systems don't stack.
  const isPortalRoute = location.pathname.startsWith("/portal");

  // /welcome is the Path-A login warm-up splash: an AUTHED, full-bleed route
  // that owns its own chrome (like LoginFiling). It is intentionally NOT in
  // `unauthenticatedRoutes` — a logged-out user hitting it should still bounce
  // to /ll97 — but it must render BARE (no SidebarNav, no PortalReturnBar) so
  // the splash fills the viewport before handing off to /portal.
  const isWelcomeRoute = location.pathname === "/welcome";

  // One decision for every bounce (lib/postLoginHome.ts): signed out -> /login, /login -> the
  // user's home, the portal gate, and a Tracker-home user on the Classic root -> /portal.
  const portalReturnFlag =
    typeof window !== "undefined" ? window.sessionStorage.getItem("portalReturnMode") : null;
  const redirect = rootRedirect({
    pathname: location.pathname,
    searchStr: location.searchStr,
    search: location.search as Record<string, unknown>,
    user,
    isSuccess,
    isUnauthRoute,
    portalReturnFlag,
  });
  // A bounce off the page still on screen: "/" stayed painted ~150 ms after navigate("/portal") (local 10-14).
  const shownRedirect = rootRedirect({
    pathname: shown.pathname,
    search: shown.search as Record<string, unknown>,
    user,
    isSuccess,
    isUnauthRoute: isUnauthPath(shown.pathname),
    portalReturnFlag,
  });
  const redirectKey = redirect ? JSON.stringify(redirect) : "";
  useEffect(() => {
    if (redirect) navigate(redirect);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the target, not its object identity
  }, [redirectKey, navigate]);

  // Render nothing until /me answers or while a bounce is due (prod 10-14: Classic flashed before /portal).
  // isPending, not isLoading: during the cache restore /me is pending but not fetching.
  if (isPending || redirect || shownRedirect) {
    return null;
  }

  // Any authed, non-unauth page — portal included. We intentionally split this
  // from `showNav`: the prod SidebarNav is hidden on /portal/* (PortalLayout
  // owns that shell), but the impersonation banner and skip-to-content MUST
  // still render there so staff don't lose their bearings while impersonating.
  const isAuthedRoute = !isUnauthRoute && !!user;
  // Portal-return: strip Classic's full chrome and render a single
  // "Back to Tracker" bar so the user stays in their Tracker mental
  // context. The hook is called above the early-return for hook-count
  // stability; the gating to "actually show the bar" happens here.
  const isPortalReturn =
    isAuthedRoute && !isPortalRoute && !isWelcomeRoute && portalReturnMode;
  // /welcome renders bare like /portal — it owns its own full-bleed chrome.
  const showNav =
    isAuthedRoute && !isPortalRoute && !isWelcomeRoute && !isPortalReturn;

  return (
    // <Sentry.ErrorBoundary> is the route-root safety net for ANY
    // uncaught React render error in the tree below — Nav, Toaster,
    // the per-route <Outlet/>, all of it. Stream G's
    // <ChatErrorBoundary> stays in place per-tool-card inside the
    // chat dispatcher; this is the catch-all for everything else.
    // Without it, Wave-17's blank-page bug (which threw above the
    // chat tree) leaves the user staring at a blank document while
    // Sentry sees nothing. The fallback render-prop hands `error` +
    // `resetError` to <RootErrorFallback>, which surfaces user-
    // visible recovery actions (Reload / Go to dashboard).
    //
    // TODO when moving to prod: keep this wrapper in place. The
    // boundary's behavior is identical across environments; only the
    // Sentry DSN env (dev vs prod project) changes. RootErrorFallback
    // already gates the dev-only error.message hint on `MODE`, so
    // building owners on the prod build will not see internals.
    //
    // ChatSidebarProvider wraps the whole tree (including the route
    // <Outlet/>) so any descendent — e.g. <ChatHeaderButton> nested
    // inside the per-page <SiteHeader> — can call useChatSidebar()
    // without throwing. Unconditional mount: the context cost is just
    // a few useState hooks, and the auth-gate stays inside individual
    // chat components (ChatTrigger / ChatHeaderButton / ChatSidebar
    // each return null when !user || !VITE_CHAT_BETA_ENABLED).
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <RootErrorFallback error={error} resetError={resetError} />
      )}
    >
    <ChatSidebarProvider>
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        {/* Staff impersonation banner — renders only when an impersonation
            session is active (reads localStorage). Not tied to showNav because
            the portal shell still needs the banner. */}
        {isAuthedRoute && <ImpersonationBanner />}
        {/* Skip-to-content: only visible when focused, WCAG 2.4.1 bypass blocks.
            Targets #main-content which renders on every authed route. */}
        {isAuthedRoute && (
          <a
            href="#main-content"
            className={
              "sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 " +
              "focus:rounded-xl focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground " +
              "focus:outline-none focus:ring-2 focus:ring-ring"
            }
          >
            Skip to main content
          </a>
        )}

        {showNav ? <Nav /> : null}
        {isPortalReturn ? <PortalReturnBar /> : null}

        {/* Padding mirrors the sidebar widths in Nav.tsx:
            - <768px: no padding (mobile top bar is above content).
            - 768-1279px: rail width = 64px = pl-16 (tablet is always rail).
            - >=1280px: pl tracks the live sidebar width via the
              `--sidebar-w-xl` CSS var that Nav.tsx writes on the html
              element (4rem when collapsed-to-rail, 15rem when expanded
              to full). This way collapsing the sidebar reclaims 176px
              of horizontal canvas instead of leaving an empty gutter.
              Default `15rem` so the first paint matches the desktop
              full-width before Nav's effect runs. */}
        <main
          id="main-content"
          tabIndex={-1}
          className={cn(
            "w-full min-h-screen focus:outline-none",
            showNav && "md:pl-16 xl:pl-[var(--sidebar-w-xl,15rem)]",
          )}
        >
          <Outlet />
        </main>
        <Toaster />
        {/*
          Wave-10 (2026-05-24): the drawer chat, Cmd-K shortcut,
          history modal, FAB, and first-run tooltip were all deleted.
          Chat lives only at the full-page `/portal/chat` route now;
          row-level entry points (the new <RowChatIcon>) navigate
          there with seedPrompt / autoSend search params instead of
          opening a sheet over the current page. The
          <ChatSidebarProvider> stays mounted so the message buffer
          + active threadId remain shared across the chat page and
          the TrackerSidebar's Conversations list.
        */}
      </div>
    </ChatSidebarProvider>
    </Sentry.ErrorBoundary>
  );
};

export const Route = createRootRouteWithContext()({
  component: RootComponent,
});
